import {
  CSSProperties,
  JSXElementConstructor,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styles from "./Accordion.module.scss";
import { Icon } from "../Icon/icon";
import { AnimatePresence, motion } from "framer-motion";

interface CommonAccordionProps {
  id: string;
  /**
   * Keeps the accordion collapsed and blocks toggling, regardless of `expanded` or `defaultExpanded`.
   * `onToggle` is not called while disabled.
   */
  disabled?: boolean;
}

interface DefaultBase {
  paragraph?: never;
  expandText?: never;
  collapseText?: never;
  collapsedHeight?: never;
  header: ReactNode;
  body: ReactNode;
  style?: {
    component?: CSSProperties;
    activator?: CSSProperties;
    body?: CSSProperties;
  };
}

interface ParagraphBase {
  paragraph: boolean;
  expandText: string;
  collapseText: string;
  /**
   * Max height of element in px when truncated. Component will automatically fit as much content as possible, then truncate.
   * @default 100px
   */
  collapsedHeight?: number;
  header?: never;
  body: string;
  style?: {
    component?: never;
    activator?: CSSProperties;
    body?: CSSProperties;
  };
}

type BaseProps = CommonAccordionProps & (DefaultBase | ParagraphBase);

type ControlledVariant = BaseProps & {
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  defaultExpanded?: never;
};

type DefaultVariant = BaseProps & {
  expanded?: never;
  onToggle?: never;
  defaultExpanded?: boolean;
};

type AccordionProps = ControlledVariant | DefaultVariant;

type AccordionBodyProps = Partial<AccordionProps> & {
  bodyContainer: RefObject<HTMLDivElement | null>;
  isExpanded: boolean;
  isParagraphBased: boolean;
  truncatedHTML: string | null;
  readMoreMarker: string;
  allContentFits: RefObject<boolean>;
  ParagraphActivator: JSXElementConstructor<object>;
};

const AccordionBody = ({
  bodyContainer,
  id,
  style,
  isExpanded,
  isParagraphBased,
  body,
  truncatedHTML,
  readMoreMarker,
  allContentFits,
  ParagraphActivator,
}: AccordionBodyProps) =>
  isParagraphBased ? (
    <div
      ref={bodyContainer}
      id={id}
      className={`${styles.body} ${isExpanded ? styles.expanded : ""}`}
      style={isExpanded ? style?.body : undefined}
      role="region"
      aria-labelledby={`_${id}-activator`}
      aria-hidden={!isExpanded && !isParagraphBased}
    >
      <p>
        <span
          dangerouslySetInnerHTML={{
            __html: isExpanded
              ? ((body ?? "") as string).replace(/\n/g, "<br/>")
              : (truncatedHTML?.split(readMoreMarker)[0] ?? ""),
          }}
        />
        {!allContentFits.current && (
          <>
            {isExpanded && <br />}
            <ParagraphActivator />
          </>
        )}
      </p>
    </div>
  ) : (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          ref={bodyContainer}
          id={id}
          className={`${styles.body} ${isExpanded ? styles.expanded : ""}`}
          role="region"
          aria-labelledby={`_${id}-activator`}
          aria-hidden={!isExpanded && !isParagraphBased}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          <div style={style?.body}>{body}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );

export function Accordion({
  id,
  paragraph,
  header,
  body,
  collapsedHeight = 100,
  expandText,
  collapseText,
  expanded,
  onToggle,
  defaultExpanded = false,
  disabled = false,
  style,
}: AccordionProps) {
  const readMoreMarker = `HHHH--<?!-${expandText}-!?)--HHHH`; // random assortment of characters so users don't accidentally include this in their content
  const bodyContainer = useRef<HTMLDivElement>(null);
  const lastSize = useRef({ width: 0, height: 0 });
  const isComputing = useRef(false);
  const allContentFits = useRef(false);
  const [truncatedHTML, setTruncatedHTML] = useState<string | null>(null);
  const [internalExpanded, setInternalExpanded] = useState(!!defaultExpanded);

  const isParagraphBased = !!paragraph;
  const isControlled = expanded !== undefined && onToggle !== undefined;
  const isExpanded = !disabled && (isControlled ? expanded : internalExpanded);

  const toggleInternalExpanded = () => {
    if (disabled) return;
    if (isControlled) {
      onToggle(!expanded);
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  const ParagraphActivator = () => (
    <button
      className={styles.paragraphActivator}
      style={style?.activator}
      aria-expanded={isExpanded}
      aria-controls={id}
      disabled={disabled}
      onClick={toggleInternalExpanded}
    >
      {isExpanded ? collapseText : expandText}
    </button>
  );

  useLayoutEffect(() => {
    const el = bodyContainer.current;
    if (!el || !isParagraphBased || !document.body.contains(el)) return;

    // If content box size has not changed, don't compute again
    const { width, height } = el.getBoundingClientRect();
    if (
      width === lastSize.current.width &&
      height === lastSize.current.height
    ) {
      return;
    }
    lastSize.current = { width, height };

    const computeTruncation = () => {
      if (isComputing.current) return;
      isComputing.current = true;
      const computedStyle = window.getComputedStyle(el);

      // Normalize \n to <br> and split on both spaces and <br>
      const normalized = body.replace(/\n/g, "<br>");
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = normalized;

      const pieces: string[] = [];
      tempDiv.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const words = (node.textContent || "").split(" ");
          words.forEach((w) => pieces.push(w));
        } else if (node.nodeName === "BR") {
          pieces.push("<br>");
        }
      });

      const testEl = document.createElement("div");
      testEl.style.position = "absolute";
      testEl.style.visibility = "hidden";
      testEl.style.whiteSpace = "normal";
      testEl.style.boxSizing = "border-box";
      testEl.style.wordWrap = "break-word";
      testEl.style.lineHeight = computedStyle.lineHeight;
      testEl.style.font = computedStyle.font;
      testEl.style.width = computedStyle.width;
      testEl.style.padding = computedStyle.padding;
      document.body.appendChild(testEl);

      // Test if full content fits without truncating
      testEl.innerHTML = pieces.join(" ").replace(/<br>/g, "<br/>");

      const fullContentHeight = testEl.getBoundingClientRect().height;
      const fitsCompletely = fullContentHeight <= collapsedHeight;
      allContentFits.current = fitsCompletely;

      if (fitsCompletely) {
        // No need to truncate
        setTruncatedHTML(normalized);
        document.body.removeChild(testEl);
        return;
      }

      // Otherwise loop over all pieces to find max content that fits within the collapsedHeight
      let low = 0;
      let high = pieces.length;
      let lastGood = "";

      const joinPieces = (count: number): string => {
        return (
          pieces.slice(0, count).join(" ").replace(/<br>/g, "<br/>") +
          "..." +
          readMoreMarker // add marker to account for the space it will take up - will be replaced with actual button when rendering
        );
      };

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        testEl.innerHTML = joinPieces(mid);

        if (testEl.getBoundingClientRect().height <= collapsedHeight) {
          lastGood = joinPieces(mid);
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      if (lastGood !== truncatedHTML) {
        setTruncatedHTML(lastGood);
      }
      document.body.removeChild(testEl);
      isComputing.current = false;
    };

    computeTruncation();

    const observer = new ResizeObserver(() => computeTruncation());
    observer.observe(el);

    return () => observer.disconnect();
  }, [bodyContainer, isParagraphBased, body, collapsedHeight]);

  const Body = (
    <AccordionBody
      bodyContainer={bodyContainer}
      id={id}
      style={style}
      isExpanded={isExpanded}
      isParagraphBased={isParagraphBased}
      body={body}
      truncatedHTML={truncatedHTML}
      readMoreMarker={readMoreMarker}
      allContentFits={allContentFits}
      ParagraphActivator={ParagraphActivator}
    />
  );

  return isParagraphBased ? (
    Body
  ) : (
    <div className={styles.container} style={style?.component}>
      <div
        id={`_${id}-activator`}
        className={`${styles.activator} ${isExpanded ? styles.expanded : ""} ${disabled ? styles.disabled : ""}`}
        style={style?.activator}
        onClick={toggleInternalExpanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleInternalExpanded();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-expanded={isExpanded}
        aria-controls={id}
      >
        <div style={{ flex: 1 }}>{header}</div>
        <Icon name="expand_more" className={styles.icon} />
      </div>
      {Body}
    </div>
  );
}
